import type { Apartment, City, Location } from '@/payload-types'

import { getApartmentHref } from '@/lib/apartments/apartmentPaths'
import { getCanonicalLocationSlug } from '@/lib/locations/legacyLocationSlugs'
import { resolveCitySlug, resolveLocationCitySlug } from '@/lib/locations/resolveLocationCitySlug'

export const defaultLocale = 'en' as const

export const locales = ['en', 'de', 'es', 'tr', 'hi', 'zh'] as const

export type Locale = (typeof locales)[number]

export function isLocale(value: string): value is Locale {
  return locales.includes(value as Locale)
}

/**
 * Payload `fallbackLocale` for CMS reads (English fallback).
 * Non-German locales may fill empty fields from English. German stays strict.
 */
export function getCmsFallbackLocale(locale?: Locale): false | typeof defaultLocale {
  if (!locale || locale === 'de') return false
  return defaultLocale
}

/** Public URL prefix for a locale. English has no prefix. */
export function getLocalePrefix(locale: Locale): string {
  return locale === defaultLocale ? '' : `/${locale}`
}

export type CMSLinkableCollection =
  | 'pages'
  | 'posts'
  | 'promotions'
  | 'cities'
  | 'locations'
  | 'apartments'

type CMSReferenceDocument = {
  slug?: string | null
  templateKey?: string | null
  city?: number | City | null
  location?: number | Location | null
}

export type TemplatePathMapLike = Partial<
  Record<'home' | 'posts' | 'promotions' | 'events' | string, string>
>

export function getCMSReferenceHref(
  relationTo: CMSLinkableCollection,
  value: CMSReferenceDocument | Apartment | City | Location | string,
  locale: Locale,
  templatePaths?: TemplatePathMapLike | null,
): string | null {
  if (typeof value === 'string') {
    const slug = value.trim()
    if (!slug) return null

    if (relationTo === 'locations' || relationTo === 'apartments') {
      return null
    }

    return getCMSReferenceHref(relationTo, { slug }, locale, templatePaths)
  }

  const slug = value.slug?.trim()
  if (!slug) return null

  if (relationTo === 'pages') {
    if (value.templateKey === 'home' || slug === 'home') {
      return localizePath('/', locale)
    }

    const mappedPath = value.templateKey ? templatePaths?.[value.templateKey] : undefined
    if (mappedPath) {
      return localizePath(mappedPath, locale)
    }

    return localizePath(`/${slug}`, locale)
  }

  if (relationTo === 'posts') {
    const archivePath = templatePaths?.posts ?? '/posts'
    return localizePath(`${archivePath}/${slug}`, locale)
  }

  if (relationTo === 'promotions') {
    const archivePath = templatePaths?.promotions ?? '/promotions'
    return localizePath(`${archivePath}/${slug}`, locale)
  }

  if (relationTo === 'cities') {
    return localizePath(`/cities/${slug}`, locale)
  }

  if (relationTo === 'locations') {
    const citySlug = resolveCitySlug(value.city)
    if (!citySlug) return null

    return localizePath(`/cities/${citySlug}/${getCanonicalLocationSlug(slug)}`, locale)
  }

  if (relationTo === 'apartments') {
    const location = 'location' in value ? value.location : null
    if (!location || typeof location === 'number') return null

    const citySlug = resolveLocationCitySlug(location)
    const locationSlug = location.slug?.trim()
    if (!citySlug || !locationSlug) return null

    return getApartmentHref(citySlug, getCanonicalLocationSlug(locationSlug), slug, locale)
  }

  return null
}

/** Build a public path for the given locale. */
export function localizePath(path: string, locale: Locale): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  const prefix = getLocalePrefix(locale)

  if (normalized === '/') {
    return prefix || '/'
  }

  return `${prefix}${normalized}`
}

/** Infer locale from the visible or internal pathname. */
export function getLocaleFromPathname(pathname: string): Locale {
  for (const locale of locales) {
    if (pathname === `/${locale}` || pathname.startsWith(`/${locale}/`)) {
      return locale
    }
  }

  return defaultLocale
}

export const localeLabels: Record<Locale, string> = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  tr: 'Türkçe',
  hi: 'हिन्दी',
  zh: '中文',
}

export function getLocaleLabel(locale: Locale): string {
  return localeLabels[locale]
}

/** Strip a locale prefix from a public or internal pathname (`/de`, `/en`). */
export function stripLocaleFromPathname(pathname: string): string {
  for (const locale of locales) {
    const prefix = `/${locale}`
    if (pathname === prefix) return '/'
    if (pathname.startsWith(`${prefix}/`)) {
      return pathname.slice(prefix.length) || '/'
    }
  }

  return pathname
}

/** Build the equivalent path for a locale, preserving query params. */
export function getLocaleHref(pathname: string, search: string, locale: Locale): string {
  const basePath = stripLocaleFromPathname(pathname)

  return `${localizePath(basePath, locale)}${search}`
}
